import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input, Label, Modal } from "reactstrap";
import { Link as LinkIcon } from "react-feather";
import moment from "moment";
import { toast } from "react-toastify";
import { MixpanelEvents, trackEvent } from "../../../utils/mixpanelTracking";
import type {
  DemoLead,
  DemoOpsFilterTabState,
  LeadSaveTouched,
} from "./demoOps.lib";
import {
  DEMO_OPS_CS_CONNECTED_REASONS,
  DEMO_OPS_CS_NOT_CONNECTED_REASONS,
  DEMO_OPS_FILTER_TAG_CONNECTED,
  DEMO_OPS_FILTER_TAG_NOT_CONNECTED,
  FilterOnField,
  buildFilterStringFromTabState,
  emptyDemoOpsFilterTabState,
  parseDemoOpsFilterForTab,
  type DemoOpsFilterTabKey,
  callStatusSelectOptions,
  getDisplayDemoStatus,
  leadDisplayName,
  teacherDisplayName,
} from "./demoOps.lib";
import { getDemoOpsDemoClassLink } from "../../../Api/apiUtils";
import {
  isDomesticIndiaLead,
  isAsiaKolkataTimezone,
  isIndiaCountryCode,
} from "./liveDemoUtils";
import ClickToCall from "../../../Telephony/ClickToCall";
import { Badge, Button, EmptyState, Loader, Select, Table } from "../../../ui";
import { Tabs } from "./ui-pending/Tabs";
import { TextArea } from "./ui-pending/TextArea";
import { DemoStatusBadge } from "./DemoStatusBadge";

interface FilterModalProps {
  isOpen: boolean;
  toggle: () => void;
  initialFilterOn: FilterOnField;
  initialFilter: string;
  onApply: (filter: string, filterOn: FilterOnField) => void;
}

export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  toggle,
  initialFilterOn,
  initialFilter,
  onApply,
}) => {
  const [activeTab, setActiveTab] = useState<DemoOpsFilterTabKey>("callStatus1");
  const [tab1, setTab1] = useState<DemoOpsFilterTabState>(() =>
    emptyDemoOpsFilterTabState()
  );
  const [tab2, setTab2] = useState<DemoOpsFilterTabState>(() =>
    emptyDemoOpsFilterTabState()
  );

  useEffect(() => {
    if (!isOpen) return;
    setTab1(parseDemoOpsFilterForTab(initialFilter, initialFilterOn, "callStatus1"));
    setTab2(parseDemoOpsFilterForTab(initialFilter, initialFilterOn, "callStatus2"));
    setActiveTab(
      initialFilterOn === "callStatus2" ? "callStatus2" : "callStatus1"
    );
  }, [isOpen, initialFilter, initialFilterOn]);

  const current = activeTab === "callStatus1" ? tab1 : tab2;

  const setCurrent = (patch: Partial<DemoOpsFilterTabState>) => {
    if (activeTab === "callStatus1") {
      setTab1((prev) => ({ ...prev, ...patch }));
    } else {
      setTab2((prev) => ({ ...prev, ...patch }));
    }
  };

  const toggleReason = (r: string) => {
    setCurrent({
      reasonChecks: {
        ...current.reasonChecks,
        [r]: !current.reasonChecks[r],
      },
    });
  };

  const clearFilters = () => {
    setTab1(emptyDemoOpsFilterTabState());
    setTab2(emptyDemoOpsFilterTabState());
  };

  const apply = () => {
    const filter = buildFilterStringFromTabState(current);
    const filterOn: FilterOnField = activeTab;
    if (!filter.trim()) {
      onApply("", "");
      toggle();
      return;
    }
    onApply(filter, filterOn);
    toggle();
  };

  const reasonGrid = (reasons: string[]) => (
    <div className="demo-ops-filter-reason-grid">
      {reasons.map((r) => (
        <Label key={r} check className="demo-ops-check">
          <Input
            type="checkbox"
            className="levelChk"
            checked={!!current.reasonChecks[r]}
            onChange={() => toggleReason(r)}
          />
          {r}
        </Label>
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      className="ds-dialog demo-ops-filter-modal"
    >
      <h2 className="ds-dialog__title">Filter Leads</h2>
      <div className="ds-stack demo-ops-filter-body">
        <Tabs
          label="Call status"
          items={[
            { key: "callStatus1" as DemoOpsFilterTabKey, label: "Call Status 1" },
            { key: "callStatus2" as DemoOpsFilterTabKey, label: "Call Status 2" },
          ]}
          active={activeTab}
          onChange={setActiveTab}
        />

        <div className="demo-ops-check-row" role="radiogroup" aria-label="Filter mode">
          <Label check className="demo-ops-check">
            <Input
              type="radio"
              className="levelChk"
              name={`demo-ops-filter-mode-${activeTab}`}
              checked={current.mode === "tags"}
              onChange={() =>
                setCurrent({
                  mode: "tags",
                  reasonChecks: {},
                })
              }
            />
            Filter by Tags
          </Label>
          <Label check className="demo-ops-check">
            <Input
              type="radio"
              className="levelChk"
              name={`demo-ops-filter-mode-${activeTab}`}
              checked={current.mode === "reasons"}
              onChange={() =>
                setCurrent({
                  mode: "reasons",
                  tagConnected: false,
                  tagNotConnected: false,
                })
              }
            />
            Filter by Reasons
          </Label>
        </div>

        {current.mode === "tags" ? (
          <div className="demo-ops-check-row">
            <Label check className="demo-ops-check">
              <Input
                type="checkbox"
                className="levelChk"
                checked={current.tagConnected}
                onChange={() =>
                  setCurrent({ tagConnected: !current.tagConnected })
                }
              />
              {DEMO_OPS_FILTER_TAG_CONNECTED}
            </Label>
            <Label check className="demo-ops-check">
              <Input
                type="checkbox"
                className="levelChk"
                checked={current.tagNotConnected}
                onChange={() =>
                  setCurrent({ tagNotConnected: !current.tagNotConnected })
                }
              />
              {DEMO_OPS_FILTER_TAG_NOT_CONNECTED}
            </Label>
          </div>
        ) : (
          <div className="ds-stack demo-ops-filter-sections">
            <section>
              <h3 className="ds-field__label demo-ops-filter-section-title">Connected Reasons</h3>
              {reasonGrid(DEMO_OPS_CS_CONNECTED_REASONS)}
            </section>
            <section>
              <h3 className="ds-field__label demo-ops-filter-section-title">Not Connected Reasons</h3>
              {reasonGrid(DEMO_OPS_CS_NOT_CONNECTED_REASONS)}
            </section>
            {(() => {
              const known = new Set([
                ...DEMO_OPS_CS_CONNECTED_REASONS,
                ...DEMO_OPS_CS_NOT_CONNECTED_REASONS,
              ]);
              const extra = Object.keys(current.reasonChecks).filter(
                (k) => current.reasonChecks[k] && !known.has(k)
              );
              if (extra.length === 0) return null;
              return (
                <section>
                  <h3 className="ds-field__label demo-ops-filter-section-title">
                    Additional values (from saved filter)
                  </h3>
                  {reasonGrid(extra)}
                </section>
              );
            })()}
          </div>
        )}
      </div>
      <div className="ds-dialog__actions">
        <Button variant="ghost" onClick={toggle}>
          Cancel
        </Button>
        <Button variant="secondary" onClick={clearFilters}>
          Clear Filters
        </Button>
        <Button variant="primary" onClick={apply}>
          Apply Filters
        </Button>
      </div>
    </Modal>
  );
};

type ViewKind = "myleads" | "allleads" | "livedemo";

function rowKey(lead: DemoLead): string {
  return String(
    lead.recordId ?? `${lead.studentId}-${lead.classScheduleId}-${lead.slotDate}`
  );
}

function formatDt(v?: string) {
  if (!v) return "—";
  const m = moment(v);
  return m.isValid() ? m.format("DD-MMM-YYYY hh:mm:ss A") : String(v);
}

function formatTzCell(v?: string) {
  if (!v) return "—";
  const m = moment(v);
  return m.isValid() ? m.format("DD-MMM-YYYY h:mm A") : String(v);
}

function classTimePrimary(lead: DemoLead): string | undefined {
  if (lead.classDateInTimeZone) return String(lead.classDateInTimeZone);
  const d = `${lead.slotDate ?? ""}`.trim();
  const t = `${lead.startTime ?? ""}`.trim();
  if (d && t) return `${d} ${t}`;
  return d || t || undefined;
}

/**
 * "Class Time" column = the slot's own time (slotDate + startTime), NOT the
 * student-timezone time, which has its own column.
 */
function formatClassTime(lead: DemoLead): string {
  const d = `${lead.slotDate ?? ""}`.trim();
  const t = `${lead.startTime ?? ""}`.trim();
  if (!d && !t) return formatDt(lead.classDateInTimeZone);

  const raw = `${d} ${t}`.trim();
  const m = moment(
    raw,
    ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD", "HH:mm:ss"],
    true
  );
  return m.isValid() ? m.format("DD-MMM-YYYY HH:mm:ss") : raw;
}

function liveStartMoment(lead: DemoLead): moment.Moment | null {
  const raw = classTimePrimary(lead);
  if (!raw) return null;
  const m = moment(raw);
  return m.isValid() ? m : null;
}

function merged(lead: DemoLead, drafts: Record<string, Partial<DemoLead>>): DemoLead {
  return { ...lead, ...(drafts[rowKey(lead)] || {}) };
}

function isDomesticCourseRow(lead: DemoLead, viewName: ViewKind): boolean {
  if (viewName !== "livedemo") return false;
  // Live Demo highlight only: Asia/Kolkata (or Calcutta) + +91.
  return isDomesticIndiaLead(lead);
}

const EDIT_FIELDS: (keyof DemoLead)[] = [
  "demoCallStatus1",
  "demoCallStatus2",
  "agentComments",
];

function pickTouched(
  before: DemoLead,
  after: DemoLead
): LeadSaveTouched {
  if (before.demoCallStatus1 !== after.demoCallStatus1) return "call1";
  if (before.demoCallStatus2 !== after.demoCallStatus2) return "call2";
  return "misc";
}

function hasDraftChanges(before: DemoLead, after: DemoLead): boolean {
  return EDIT_FIELDS.some((f) => String(before[f] ?? "") !== String(after[f] ?? ""));
}


export interface LeadsTableProps {
  viewName: ViewKind;
  leads: DemoLead[];
  loading: boolean;
  hideIndiaMobile?: boolean;
  liveNow?: number;
  onRowOpenDetail: (lead: DemoLead) => void;
  onSaveRow: (
    before: DemoLead,
    after: DemoLead,
    touched: LeadSaveTouched,
    viewName: ViewKind
  ) => Promise<void>;
}

export const LeadsTable: React.FC<LeadsTableProps> = ({
  viewName,
  leads,
  loading,
  hideIndiaMobile,
  liveNow,
  onRowOpenDetail,
  onSaveRow,
}) => {
  const [drafts, setDrafts] = useState<Record<string, Partial<DemoLead>>>({});
  const linkCache = useRef<Map<string, string>>(new Map());
  const [saveConfirm, setSaveConfirm] = useState<{
    before: DemoLead;
    after: DemoLead;
  } | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);

  const filtered = useMemo(() => {
    // My Leads / All Leads only: hide Asia/Kolkata + +91.
    // Keep Asia/Kolkata when number is international (!=+91).
    const rows = hideIndiaMobile
      ? leads.filter((l) => !(isAsiaKolkataTimezone(l) && isIndiaCountryCode(l)))
      : leads;
    if (viewName === "livedemo") return rows;
    // Latest class (slot date + start time) first.
    const key = (l: DemoLead) => `${l.slotDate ?? ""} ${l.startTime ?? ""}`;
    return [...rows].sort((a, b) => key(b).localeCompare(key(a)));
  }, [leads, hideIndiaMobile, viewName]);

  const showActions = viewName !== "livedemo";
  const editable = viewName !== "livedemo";

  const patchDraft = (lead: DemoLead, patch: Partial<DemoLead>) => {
    const k = rowKey(lead);
    setDrafts((prev) => ({
      ...prev,
      [k]: { ...(prev[k] || {}), ...patch },
    }));
  };

  const copyDemoLink = async (lead: DemoLead) => {
    const cid = lead.classScheduleId;
    const sid = lead.studentId;
    if (!cid || !sid) {
      toast.error("Missing class or student id for link.");
      return;
    }
    const cacheKey = `${cid}:${sid}`;
    try {
      let url = linkCache.current.get(cacheKey);
      if (!url) {
        url = await getDemoOpsDemoClassLink(cid, sid);
        if (url) linkCache.current.set(cacheKey, url);
      }
      if (!url) throw new Error("empty");
      await navigator.clipboard.writeText(url);
      toast.success("Demo link copied.");
    } catch {
      toast.error("Could not copy demo link.");
    }
  };

  const runSave = async () => {
    if (!saveConfirm) return;
    setSaveBusy(true);
    try {
      const touched = pickTouched(saveConfirm.before, saveConfirm.after);
      await onSaveRow(saveConfirm.before, saveConfirm.after, touched, viewName);
      setDrafts((prev) => {
        const nk = { ...prev };
        delete nk[rowKey(saveConfirm.before)];
        return nk;
      });
      toast.success("Lead updated.");
      trackEvent(MixpanelEvents.DEMOOPS_LEAD_SAVE_SUCCESS, {
        demo_ops_source: "leads_table",
        demo_ops_view: viewName,
        demo_ops_touched: touched,
        record_id:
          saveConfirm.before.recordId != null
            ? String(saveConfirm.before.recordId)
            : "",
        student_id:
          saveConfirm.before.studentId != null
            ? String(saveConfirm.before.studentId)
            : "",
      });
    } catch {
      toast.error("Save failed.");
      trackEvent(MixpanelEvents.DEMOOPS_LEAD_SAVE_FAILED, {
        demo_ops_source: "leads_table",
        demo_ops_view: viewName,
        record_id:
          saveConfirm.before.recordId != null
            ? String(saveConfirm.before.recordId)
            : "",
        student_id:
          saveConfirm.before.studentId != null
            ? String(saveConfirm.before.studentId)
            : "",
      });
    } finally {
      setSaveBusy(false);
      setSaveConfirm(null);
    }
  };

  const liveMoment = liveNow ? moment(liveNow) : moment();

  return (
    <div className="demo-ops-scroll">
      {loading ? (
        <Loader label="Loading leads…" />
      ) : (
        <Table
          className={viewName === "livedemo" ? "demo-ops-live" : undefined}
          caption="Demo leads"
        >
          <thead>
            <tr>
              <th>#</th>
              {viewName === "allleads" ? <th>Agent Name</th> : null}
              <th>Student Name</th>
              <th>Age Group</th>
              <th>Teacher Name</th>
              <th>Course Name</th>
              {viewName === "livedemo" ? <th>Country</th> : null}
              <th>Timezone</th>
              <th>Contact</th>
              <th>Class Time (student TZ)</th>
              <th>Class Time</th>
              <th>Demo Status</th>
              <th>Demo Call Status 1</th>
              <th>Demo Call Status 2</th>
              <th>Latest Call Status</th>
              {editable ? (
                <>
                  <th>Comments</th>
                  <th>{showActions ? "Actions" : ""}</th>
                </>
              ) : (
                <>
                  <th>Elapsed</th>
                  <th>{showActions ? "Actions" : ""}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={24}>
                  <EmptyState
                    title="No rows to display."
                    description="Try another date range or clear the call-status filter."
                  />
                </td>
              </tr>
            ) : (
              filtered.map((raw, idx) => {
                const lead = merged(raw, drafts);
                const k = rowKey(raw);
                const draftAfter = merged(raw, drafts);
                const hasRecordId = !!raw.recordId;
                const canEditRow = editable && hasRecordId;
                const dirty = canEditRow && hasDraftChanges(raw, draftAfter);

                let elapsed = "";
                if (viewName === "livedemo") {
                  const start = liveStartMoment(lead);
                  if (
                    `${lead.demoStatus ?? ""}`.toUpperCase() === "ONGOING" &&
                    start?.isValid()
                  )
                    elapsed = `${liveMoment.diff(start, "minutes")}m`;
                }

                const displayStatus = getDisplayDemoStatus(lead);

                const teacherCell =
                  `${lead.classStatus ?? ""}`.toLowerCase() === "found" ? (
                    <Badge tone="warning">AE not found</Badge>
                  ) : (
                    teacherDisplayName(lead)
                  );

                const trClass = `${
                  viewName === "livedemo" && isDomesticCourseRow(lead, viewName)
                    ? "domestic-call-row"
                    : ""
                }`;

                return (
                  <tr
                    key={k}
                    className={`demo-ops-row-clickable ${trClass}`.trim()}
                    onClick={() => onRowOpenDetail(lead)}
                  >
                    <td className="ds-num" onClick={(e) => e.stopPropagation()}>{idx + 1}</td>
                    {viewName === "allleads" ? (
                      <td>{lead.agentName ?? "—"}</td>
                    ) : null}
                    <td>
                      <div className="ds-stack demo-ops-student-cell">
                        <span>{leadDisplayName(lead)}</span>
                        {lead.trialType && (
                          <Badge tone="accent" solid>{lead.trialType}</Badge>
                        )}
                      </div>
                    </td>
                    <td>{lead.ageGroup ?? "—"}</td>
                    <td>{teacherCell}</td>
                    <td>{lead.courseName ?? "—"}</td>
                    {viewName === "livedemo" ? (
                      <td>{lead.country ?? "—"}</td>
                    ) : null}
                    <td>{lead.studentTimezone ?? "—"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <ClickToCall
                        number={
                          lead.mobileNumber
                            ? `${lead.countryCode ?? ""}${lead.mobileNumber}`
                            : undefined
                        }
                        name={leadDisplayName(lead)}
                        sourceRecordId={
                          lead.recordId != null ? String(lead.recordId) : undefined
                        }
                        customerUserId={
                          lead.userId != null ? String(lead.userId) : undefined
                        }
                      />
                    </td>
                    <td>{formatTzCell(lead.classDateInTimeZone)}</td>
                    <td>{formatClassTime(lead)}</td>
                    <td>
                      <DemoStatusBadge status={displayStatus} suffix={elapsed || undefined} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {editable ? (
                        <Select
                          size="sm"
                          aria-label="Demo call status 1"
                          className="demo-ops-cell-select"
                          disabled={!canEditRow}
                          value={String(lead.demoCallStatus1 ?? "")}
                          onChange={(e) =>
                            patchDraft(raw, { demoCallStatus1: e.target.value })
                          }
                          options={callStatusSelectOptions(lead.demoCallStatus1).map(
                            (o) => ({ value: o, label: o || "—" })
                          )}
                        />
                      ) : (
                        lead.demoCallStatus1 ?? "—"
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {editable ? (
                        <Select
                          size="sm"
                          aria-label="Demo call status 2"
                          className="demo-ops-cell-select"
                          disabled={!canEditRow}
                          value={String(lead.demoCallStatus2 ?? "")}
                          onChange={(e) =>
                            patchDraft(raw, { demoCallStatus2: e.target.value })
                          }
                          options={callStatusSelectOptions(lead.demoCallStatus2).map(
                            (o) => ({ value: o, label: o || "—" })
                          )}
                        />
                      ) : (
                        lead.demoCallStatus2 ?? "—"
                      )}
                    </td>
                    <td>{lead.demoCallStatus2 || lead.demoCallStatus1 || "—"}</td>
                    {editable ? (
                      <>
                        <td onClick={(e) => e.stopPropagation()}>
                          <TextArea
                            size="sm"
                            rows={1}
                            aria-label="Comments"
                            disabled={!canEditRow}
                            className="demo-ops-comment-field"
                            value={String(lead.agentComments ?? "")}
                            onChange={(e) =>
                              patchDraft(raw, { agentComments: e.target.value })
                            }
                          />
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="demo-ops-row-actions">
                            <Button
                              variant="primary"
                              size="sm"
                              disabled={!dirty || !canEditRow}
                              onClick={() =>
                                setSaveConfirm({ before: raw, after: draftAfter })
                              }
                            >
                              Save
                            </Button>
                            {showActions ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<LinkIcon size={14} aria-hidden />}
                                onClick={() => copyDemoLink(raw)}
                                title="Copy join link"
                                aria-label="Copy join link"
                              />
                            ) : null}
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{elapsed}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => copyDemoLink(lead)}
                          >
                            Copy link
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      <Modal
        isOpen={!!saveConfirm}
        toggle={() => !saveBusy && setSaveConfirm(null)}
        centered
        className="ds-dialog"
      >
        <h2 className="ds-dialog__title">Save changes</h2>
        <div className="ds-dialog__body">Confirm update for this demo lead?</div>
        <div className="ds-dialog__actions">
          <Button
            variant="secondary"
            disabled={saveBusy}
            onClick={() => setSaveConfirm(null)}
          >
            Cancel
          </Button>
          <Button variant="primary" loading={saveBusy} onClick={runSave}>
            Confirm
          </Button>
        </div>
      </Modal>
    </div>
  );
};
